import { useRef } from 'react'

import EditorMenuControls from './EditorMenuControls.tsx'
import { RichTextEditor, type RichTextEditorRef, LinkBubbleMenu, ResizableImage, TableImproved, FontSize, TableBubbleMenu, LinkBubbleMenuHandler } from "mui-tiptap"
import StarterKit from "@tiptap/starter-kit"
import { TextStyle } from '@tiptap/extension-text-style'
import FontFamily from '@tiptap/extension-font-family'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import { Color } from '@tiptap/extension-color'
// import Highlight from '@tiptap/extension-highlight'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import BubbleMenu from '@tiptap/extension-bubble-menu'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'

import { Today, WeekFromToday } from '../utils.ts'
import { CurrentUser } from '../services/SessionService.ts'
import { getUsers } from '../hooks/useUserNames.ts'

export type FancyTextEditorProps = {
    editable: boolean, 
    [rest: string]: any,
}

let rteRef: React.MutableRefObject<RichTextEditorRef | null>


export const generalValues = () => {
  const loginUser = CurrentUser()

  const general = {
    Today: Today(), 
    WeekFromToday: WeekFromToday(),
    User: loginUser.fullName, 
    UserEmail: loginUser.email,
    UserPhone: '',
    UserTitle: '',
    Company: "Legal25",
    Filename: "Filename",
    DocumentId: "DocumentId",
    Signature: "Signature",
    SquareLogo: '<img src="https://app.gaylordcases.com/thumbnail_Legal25 favicon blue 192.png"/>',
    BannerLogo: '<img src="https://app.gaylordcases.com/legal25-banner-small.png"/>',
  }

  getUsers().then((users: any) => {
    const user = users.find((u: any) => u.displayName === loginUser?.fullName)
    if (user) {
      general.UserPhone = user.mobilePhone ?? ''
      general.UserTitle = user.jobTitle ?? ''
    } 
  })

  return general
}
    
export const insertField = (fieldName: string = "") => {
    rteRef.current?.editor?.commands.insertContent("{{" + fieldName + "}}")
}

export const insert = (text: string = "") => {
    rteRef.current?.editor?.commands.insertContent(text)
}

export const getContent = () => {
    return rteRef.current?.editor?.getHTML() ?? ''
}

export const setContent = (content: string = "") => {
    rteRef.current?.editor?.commands.setContent(content)
}

export const focus = (to: string = "") => {
    rteRef.current?.editor?.commands.focus(to as any)
}

export const setContentFieldsSubject = (content: string, values: any | null = null) => {
    const result = getContentFieldsSubject(content, values)
    setContent(result.html)
    return result
}

export const getContentFieldsSubject = (content: string, values: any | null = null) => {
    let html = content

    function insertFields(o: any, parentPath: string = '') {
        for (let key in o) {
            const p = parentPath + key
            const v = o[key]

            if (typeof v === 'object') {
                insertFields(v, key + '.')
            } else {
                html = html.replaceAll("{{" + p + "}}", v?.toString() ?? "*** Missing ***")
            }
        }
    }
    
    if (values) {
        // Replace all {{fieldName}} with the value from values
        insertFields(values)
    }

    // If Subject found, strip it and return it
    let subject = ""
    if (html.startsWith("<p>Subject:")) {
      subject = html.split("<p>Subject: [")[1]?.split("]</p>")[0]
      html = html.replace("<p>Subject: [" + subject + "]</p>", "")
    }

    return {html: html, subject: subject}
}

export default ({ editable, ...rest }: FancyTextEditorProps) => {
    rteRef = useRef<RichTextEditorRef>(null)

    return (
        <RichTextEditor
            ref={rteRef}
            editable={editable}
            extensions={extensions} 
            autofocus={"end"}
            // content={selectedTemplate?.Template ?? ""}
            renderControls={() => (<EditorMenuControls />)}>
            {/* {() => (<><LinkBubbleMenu /><TableBubbleMenu /></>)} */}
        </RichTextEditor>
    )
}

const linkConfig = {
    openOnClick: false,
    autolink: true,
    defaultProtocol: 'https',
    protocols: ['http', 'https'],
    isAllowedUri: (url: any, ctx: any) => {
      try {
        // construct URL
        const parsedUrl = url.includes(':') ? new URL(url) : new URL(`${ctx.defaultProtocol}://${url}`)
  
        // use default validation
        if (!ctx.defaultValidate(parsedUrl.href)) {
          return false
        }
  
        // disallowed protocols
        const disallowedProtocols = ['ftp', 'file', 'mailto']
        const protocol = parsedUrl.protocol.replace(':', '')
  
        if (disallowedProtocols.includes(protocol)) {
          return false
        }
  
        // only allow protocols specified in ctx.protocols
        const allowedProtocols = ctx.protocols.map((p: any) => (typeof p === 'string' ? p : p.scheme))
  
        if (!allowedProtocols.includes(protocol)) {
          return false
        }
  
        // disallowed domains
        const disallowedDomains = ['example-phishing.com', 'malicious-site.net']
        const domain = parsedUrl.hostname
  
        if (disallowedDomains.includes(domain)) {
          return false
        }
  
        // all checks have passed
        return true
      } catch {
        return false
      }
    },
    shouldAutoLink: (url: any) => {
      try {
        // construct URL
        const parsedUrl = url.includes(':') ? new URL(url) : new URL(`https://${url}`)
  
        // only auto-link if the domain is not in the disallowed list
        const disallowedDomains = ['example-no-autolink.com', 'another-no-autolink.com']
        const domain = parsedUrl.hostname
  
        return !disallowedDomains.includes(domain)
      } catch {
        return false
      }
    },
  
  }
  
  const extensions = [TableImproved.configure({resizable: true}), TableRow, TableHeader, TableCell,
    StarterKit, TextStyle, FontFamily, Color, Underline, Link.configure(linkConfig),
    BubbleMenu.configure({ pluginKey: 'bubbleMenu' }), ResizableImage, //LinkBubbleMenuHandler, LinkBubbleMenu,
    FontSize, TextAlign.configure({types: ['heading', 'paragraph', "image"]}),
    Image.configure({inline: true})
  ]
  // const extensions = [TableImproved.configure({resizable: true}), TableRow, TableHeader, TableCell,
  //   StarterKit, TextStyle, FontFamily, Color, Underline, Highlight.configure({ multicolor: true }),
  //   BubbleMenu.configure({ pluginKey: 'bubbleMenu' }), Link.configure(linkConfig), ResizableImage, 
  //   LinkBubbleMenuHandler, FontSize, TextAlign.configure({types: ['heading', 'paragraph', "image"]}),
  //   Image.configure({inline: true})
  // ]
  
//     HardBreak.extend({
//     addKeyboardShortcuts() {
//         return {
//             Enter: () => {
//                 if (this.editor.isActive('orderedList') || this.editor.isActive('bulletList')) {
//                     return this.editor.chain().createParagraphNear().run();
//                 }
//                 return this.editor.commands.setHardBreak();
//             },
//         };
//     },
// }),

// addKeyboardShortcuts() {
//     return {
//       'Mod-Enter': () => this.editor.commands.setHardBreak(),
//       'Shift-Enter': () => this.editor.commands.setHardBreak(),
//     }
//   },
